import { Fragment, ReactNode, useState } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import toast from 'react-hot-toast'
import { twJoin, twMerge } from 'tailwind-merge'
import Confirmation from 'shared/components/confirmation-modal'
import { featureFlagsClient } from 'shared/feature-flags/client'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import CloseIcon from 'shared/icons/close-icon'
import PenOutlineIcon from 'shared/icons/pen-outline-icon'
import ReportIcon from 'shared/icons/report-icon'
import ThreeDotsHorizontalIcon from 'shared/icons/three-dots-horizontal-icon'
import TrashOutlineIcon from 'shared/icons/trash-outline-icon'
import { useReportPost } from 'modules/community/api/report-post'
import { PostUpdateModal } from 'modules/community/components/manage-post/update-post'
import { ReportContentModal } from 'modules/community/components/report-content/report-content-modal'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { RemoveRecentActivitiesCheckbox } from 'modules/community/post/remove-recent-activities-checkbox'
import type { PostInterface } from 'modules/community/types/post-interface'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'

interface PostActionsMenuProps {
  post: PostInterface
  deletePost?: () => Promise<void>
  deletePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  declinePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  className?: string
}

export function PostActionsMenu({
  post,
  deletePost,
  deletePostAndBanUser,
  declinePostAndBanUser,
  className,
}: PostActionsMenuProps): ReactNode {
  const { user } = useUser()
  const { member, isUserMember } = useIsUserMember()
  const { t } = useTranslation()
  const router = useRouter()
  const communityPath =
    typeof router.query.communityPath === 'string' ? router.query.communityPath : ''

  const [isRemovePostOpened, setIsRemovePostOpened] = useState(false)
  const [isRemovePostAndBanUserOpened, setIsRemovePostAndBanUserOpened] = useState(false)
  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [isDeclinePostAndBanUserOpened, setIsDeclinePostAndBanUserOpened] = useState(false)
  const [isOpenPostUpdate, setIsOpenPostUpdate] = useState(false)
  const [isReportOpened, setIsReportOpened] = useState(false)

  const reportPostMutation = useReportPost({
    communityPath,
    postId: post.id,
  })

  const [isPostUpdatable, isPostDeletable, isPostAuthorBannable, isPostDeclinable] = hasPermissions(
    user,
    'posts',
    ['update', 'delete', 'ban', 'decline'],
    {
      post,
      member,
    },
  )

  const isPostDeclinableAndAuthorBannable = isPostDeclinable && isPostAuthorBannable

  const isOwnPost =
    Boolean(user && isAuthorizedUser(user) && user.id === post.userId) ||
    member?.userId === post.userId

  const isReportsEnabled = featureFlagsClient.isOn(featureFlagKeys.enableCommunityReports)
  const canReportPost =
    isReportsEnabled &&
    Boolean(isUserMember) &&
    Boolean(communityPath) &&
    post.status === PostStatusEnum.Approved &&
    !isOwnPost

  if (
    !(
      isPostUpdatable ||
      isPostDeletable ||
      isPostAuthorBannable ||
      isPostDeclinableAndAuthorBannable ||
      canReportPost
    )
  ) {
    return null
  }

  return (
    <>
      <Menu as="div" className="relative flex">
        <MenuButton className="flex size-10 flex-row items-center justify-center rounded-full text-text-secondary transition-colors duration-300 hover:bg-text-secondary/15 hover:text-brand-primary">
          <ThreeDotsHorizontalIcon className="size-8" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems
            as="ul"
            anchor="bottom end"
            className={twMerge(
              `z-10 flex w-max max-w-sm min-w-36 flex-col rounded-[10px] bg-background-secondary px-5 py-3 shadow-menu`,
              className,
            )}
          >
            {isPostDeletable && deletePost && (
              <MenuItem as="li">
                {({ focus }) => (
                  <button
                    type="button"
                    className={twJoin(
                      'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm',
                      focus
                        ? 'text-text-primary/70'
                        : 'text-text-primary hover:text-text-primary/70',
                    )}
                    onClick={() => {
                      setIsRemovePostOpened(true)
                    }}
                  >
                    <CloseIcon className="size-5 fill-text-primary group-hover:fill-text-primary/70" />
                    {t('community.actions.remove_post.label')}
                  </button>
                )}
              </MenuItem>
            )}
            {isPostAuthorBannable && deletePostAndBanUser && (
              <MenuItem as="li">
                {({ focus }) => (
                  <button
                    type="button"
                    className={twJoin(
                      'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm',
                      focus
                        ? 'text-text-primary/70'
                        : 'text-text-primary hover:text-text-primary/70',
                    )}
                    onClick={() => {
                      setIsRemovePostAndBanUserOpened(true)
                    }}
                  >
                    <TrashOutlineIcon className="size-5" />
                    {t('community.actions.delete_and_ban_user.label')}
                  </button>
                )}
              </MenuItem>
            )}
            {isPostDeclinableAndAuthorBannable && declinePostAndBanUser && (
              <MenuItem as="li">
                {({ focus }) => (
                  <button
                    type="button"
                    className={twJoin(
                      'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary hover:text-text-primary/70 focus:text-text-primary/70',
                      focus && 'text-text-primary/70',
                    )}
                    onClick={() => {
                      setIsDeclinePostAndBanUserOpened(true)
                    }}
                  >
                    <TrashOutlineIcon className="size-5" />
                    {t('community.actions.decline_and_ban_user.label')}
                  </button>
                )}
              </MenuItem>
            )}
            {isPostUpdatable && (
              <MenuItem as="li">
                <button
                  type="button"
                  className="flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary hover:text-text-primary/70 focus:text-text-primary/70"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsOpenPostUpdate(true)
                  }}
                >
                  <PenOutlineIcon className="size-5" />
                  {t('community.actions.update.label')}
                </button>
              </MenuItem>
            )}
            {canReportPost && (
              <MenuItem as="li">
                {({ focus }) => (
                  <button
                    type="button"
                    className={twJoin(
                      'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary hover:text-text-primary/70 focus:text-text-primary/70',
                      focus && 'text-text-primary/70',
                    )}
                    onClick={() => {
                      setIsReportOpened(true)
                    }}
                  >
                    <ReportIcon className="size-5" />
                    {t('community.report.menu_label')}
                  </button>
                )}
              </MenuItem>
            )}
          </MenuItems>
        </Transition>
      </Menu>
      {isRemovePostOpened && (
        <Confirmation
          onConfirm={() => {
            void deletePost?.()
              .then(() => {
                setIsRemovePostOpened(false)
                toast.success(t('community.actions.remove_post.success'))
              })
              .catch(error => {
                toast.error(t('community.actions.remove_post.failed'))
                throw error
              })
          }}
          onCloseModal={() => {
            setIsRemovePostOpened(false)
          }}
          confirmationContent={t('settings.form.delete_post_confirmation.label')}
        />
      )}
      {isRemovePostAndBanUserOpened && (
        <Confirmation
          onConfirm={() => {
            void deletePostAndBanUser?.(removeRecentActivitiesChecked)
              .then(() => {
                toast.success(t('community.actions.remove_post_and_ban_user.success'))
                setIsRemovePostAndBanUserOpened(false)
              })
              .catch(error => {
                toast.error(t('community.actions.remove_post_and_ban_user.failed'))
                throw error
              })
          }}
          onCloseModal={() => {
            setIsRemovePostAndBanUserOpened(false)
            setRemoveRecentActivitiesChecked(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.delete_post_and_ban_user_confirmation.label')}
              memberDisplayName={post.author?.displayName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isDeclinePostAndBanUserOpened && (
        <Confirmation
          onConfirm={() => {
            void declinePostAndBanUser?.(removeRecentActivitiesChecked)
              .then(() => {
                toast.success(t('community.actions.decline_post_and_ban_user.success'))
                setIsDeclinePostAndBanUserOpened(false)
              })
              .catch(error => {
                toast.error(t('community.actions.decline_post_and_ban_user.failed'))
                throw error
              })
          }}
          onCloseModal={() => {
            setIsDeclinePostAndBanUserOpened(false)
            setRemoveRecentActivitiesChecked(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.decline_post_and_ban_user_confirmation.label')}
              memberDisplayName={post.author?.displayName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isOpenPostUpdate && (
        <PostUpdateModal
          post={post}
          open={isOpenPostUpdate}
          onClose={() => {
            setIsOpenPostUpdate(false)
          }}
        />
      )}
      <ReportContentModal
        opened={isReportOpened}
        onClose={() => {
          setIsReportOpened(false)
        }}
        targetType="post"
        isSubmitting={reportPostMutation.isPending}
        onSubmit={async body => {
          await reportPostMutation.mutateAsync(body)
        }}
      />
    </>
  )
}
